import { useState, useEffect } from "react";
import { api, formatApiError, cantiereComune } from "../lib/api";
import { toast } from "sonner";
import { MONTHS_IT } from "../lib/constants";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Gauge, Sparkles, Loader2, SunMedium, BatteryCharging, Pencil, X, MapPin, CalendarDays, Boxes, FileDown, Zap, AlertTriangle } from "lucide-react";
import LeadFornitura from "./LeadFornitura";

const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT"));

const PERIODO_LABEL = { annuo: "annuale", mensile: "mensile", bimestrale: "bimestrale" };
const PERIODO_FATTORE = { mensile: "×12", bimestrale: "×6" };

const ORIGINE_BADGE = {
  reale: "border-emerald-500/40 text-emerald-500",
  calcolato: "border-sky-500/40 text-sky-500",
  ricostruito: "border-sky-500/40 text-sky-500",
  stimato: "border-amber-500/40 text-amber-500",
};
const AFFID_BADGE = { Alta: "border-emerald-500/50 text-emerald-500", Media: "border-amber-500/50 text-amber-500", Bassa: "border-red-500/50 text-red-500" };

const OriginePill = ({ o }) => (
  <Badge variant="outline" className={`font-normal text-[10px] capitalize ${ORIGINE_BADGE[o] || "border-border text-muted-foreground"}`}>{o}</Badge>
);

function EnergyReport({ ea }) {
  const af = ea.affidabilita || {};
  const fasce = ea.fasce || {};
  const tot = ea.totali || {};
  const mensile = ea.mensile || [];
  const maxCons = Math.max(...mensile.map((m) => m.consumo_kwh || 0), 1);
  const dp = (x) => (x && typeof x === "object" ? x.valore : x);
  return (
    <Card className="rounded border-border p-5" data-testid="energy-report">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h2 className="font-heading font-semibold flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" strokeWidth={1.5} /> Report energetico</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Affidabilità</span>
          <Badge variant="outline" className={`font-bold ${AFFID_BADGE[af.livello] || ""}`} data-testid="energy-affidabilita">{af.indice_pct}% · {af.livello}</Badge>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4 capitalize">Tipologia: {ea.tipologia_cliente} · Motore {ea.versione_motore}</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[["Consumo annuo", tot.consumo_annuo, "kWh"], ["Impianto", tot.pv_kwp, "kWp"], ["Accumulo", tot.storage_kwh, "kWh"], ["Prezzo energia", tot.prezzo_energia_kwh, "€/kWh"]].map(([label, d, u]) => (
          <div key={label} className="border border-border rounded p-3" data-testid={`energy-tot-${label}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-1 text-xl font-heading font-bold">{fmt(dp(d))} <span className="text-xs font-normal text-muted-foreground">{u}</span></div>
            {d?.origine && <div className="mt-1"><OriginePill o={d.origine} /></div>}
          </div>
        ))}
      </div>

      {/* Grafico mensile */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Consumo mensile</div>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />reale</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sky-500" />ricostruito</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" />stimato</span>
          </div>
        </div>
        <div className="flex items-end gap-1 h-28" data-testid="energy-monthly-chart">
          {mensile.map((m) => {
            const color = m.origine === "reale" ? "bg-emerald-500" : m.origine === "ricostruito" ? "bg-sky-500" : "bg-amber-500";
            return (
              <div key={m.mese} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${m.nome}: ${fmt(m.consumo_kwh)} kWh (${m.origine})`}>
                <div className={`w-full rounded-t ${color}`} style={{ height: `${Math.max((m.consumo_kwh / maxCons) * 100, 3)}%` }} />
                <div className="text-[9px] text-muted-foreground">{m.nome}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribuzione fasce */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Distribuzione fasce</div>
          <OriginePill o={fasce.origine} />
        </div>
        <div className="flex h-6 rounded overflow-hidden border border-border" data-testid="energy-fasce-bar">
          {[["F1", fasce.f1_pct, "bg-primary"], ["F2", fasce.f2_pct, "bg-sky-500"], ["F3", fasce.f3_pct, "bg-indigo-500"]].map(([k, pct, col]) => (
            <div key={k} className={`${col} flex items-center justify-center text-[10px] font-bold text-black`} style={{ width: `${pct || 0}%` }}>{pct > 8 ? `${k} ${pct}%` : ""}</div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
          <div>F1: <b>{fmt(fasce.f1_kwh)}</b> kWh ({fasce.f1_pct}%)</div>
          <div>F2: <b>{fmt(fasce.f2_kwh)}</b> kWh ({fasce.f2_pct}%)</div>
          <div>F3: <b>{fmt(fasce.f3_kwh)}</b> kWh ({fasce.f3_pct}%)</div>
        </div>
      </div>

      {/* Anomalie e mesi mancanti */}
      {(ea.mesi_mancanti?.length > 0 || ea.anomalie?.length > 0) && (
        <div className="space-y-2" data-testid="energy-anomalie">
          {ea.mesi_mancanti?.length > 0 && (
            <div className="text-xs text-amber-600 flex items-start gap-2"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{ea.mesi_mancanti.length} mesi non presenti in bolletta: stimati automaticamente.</span></div>
          )}
          {(ea.anomalie || []).filter((a) => a.tipo !== "mesi_mancanti").map((a, i) => (
            <div key={i} className="text-xs text-muted-foreground flex items-start gap-2"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" /><span><b className="capitalize">{a.tipo.replace(/_/g, " ")}</b>: {a.dettaglio}</span></div>
          ))}
        </div>
      )}
    </Card>
  );
}


export default function LeadAnalysis({ lead, onUpdate }) {
  const analysis = lead.analysis;
  const hasBolletta = (lead.attachments || []).some((a) => a.category === "bolletta");
  const [analyzing, setAnalyzing] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geo, setGeo] = useState(false);
  const [dem, setDem] = useState(false);
  const [addr, setAddr] = useState(analysis?.pvgis?.location || cantiereComune(lead) || "");
  const [form, setForm] = useState({ f1_kwh: "", f2_kwh: "", f3_kwh: "", prezzo_energia_kwh: "", potenza_contatore_kw: "" });
  const [components, setComponents] = useState(null);

  useEffect(() => {
    if (analysis?.pv_kwp) {
      api.get("/sizing", { params: { pv_kwp: analysis.pv_kwp, storage_kwh: analysis.storage_kwh || 0 } })
        .then((r) => setComponents(r.data))
        .catch(() => setComponents(null));
    } else {
      setComponents(null);
    }
  }, [analysis?.pv_kwp, analysis?.storage_kwh]);

  useEffect(() => {
    if (!analyzing) return;
    setCountdown(120);
    const id = setInterval(() => setCountdown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [analyzing]);
  const countLabel = `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`;

  const call = async (fn, setBusy) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const analyze = () => call(async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/analyze-bill`);
      onUpdate(data);
      toast.success("Bolletta analizzata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, setAnalyzing);

  const runPvgis = () => call(async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/pvgis`, { address: addr || undefined });
      onUpdate(data);
      toast.success("Calcolo PVGIS completato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, setGeo);

  const openEdit = () => {
    setForm({ f1_kwh: analysis?.f1_kwh ?? "", f2_kwh: analysis?.f2_kwh ?? "", f3_kwh: analysis?.f3_kwh ?? "", prezzo_energia_kwh: analysis?.prezzo_energia_kwh ?? "", potenza_contatore_kw: analysis?.potenza_contatore_kw ?? "" });
    setEditing(true);
  };

  const recalc = () => call(async () => {
    try {
      const payload = {
        f1_kwh: form.f1_kwh === "" ? null : Number(form.f1_kwh),
        f2_kwh: form.f2_kwh === "" ? null : Number(form.f2_kwh),
        f3_kwh: form.f3_kwh === "" ? null : Number(form.f3_kwh),
        prezzo_energia_kwh: form.prezzo_energia_kwh === "" ? null : Number(form.prezzo_energia_kwh),
        potenza_contatore_kw: form.potenza_contatore_kw === "" ? null : Number(form.potenza_contatore_kw),
        periodo: "annuo",
      };
      const { data } = await api.put(`/leads/${lead.id}/analysis`, payload);
      onUpdate(data);
      setEditing(false);
      toast.success("Analisi ricalcolata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, setSaving);

  const isPvgis = analysis?.yield_source === "PVGIS";
  const monthly = analysis?.monthly_analysis;
  const monthlyCons = analysis?.monthly_consumption;

  return (
    <div className="space-y-5">
      <Card className="rounded border-border p-5" data-testid="lead-analysis">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-semibold flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" strokeWidth={1.5} /> Analisi Preliminare</h2>
          {analysis && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-border font-normal text-xs">{analysis.model === "manuale" ? "Manuale" : `AI · ${analysis.model}`}</Badge>
              {!editing && <Button variant="ghost" size="sm" onClick={openEdit} data-testid="edit-analysis-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Modifica</Button>}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">Consumi per fascia oraria (kWh/anno) e dimensionamento di massima.</p>

        {!analysis && !editing && (
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-muted-foreground">{hasBolletta ? "Estrai automaticamente F1, F2 e F3 dalla bolletta caricata." : "Carica una bolletta negli allegati per avviare l'analisi."}</p>
            <div className="flex items-center justify-center gap-2">
              <Button onClick={analyze} disabled={!hasBolletta || analyzing} data-testid="analyze-bill-btn" className="font-semibold">
                {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Analizza bolletta
              </Button>
              <Button variant="outline" onClick={openEdit} data-testid="manual-analysis-btn">Inserisci manualmente</Button>
            </div>
            {analyzing && (
              <div className="mx-auto max-w-md flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-600 dark:text-amber-400" data-testid="analisi-alert" role="alert">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Analisi della bolletta in corso: <b>potrebbero volerci fino a 120 secondi</b>. Non chiudere la pagina.<span className="mt-1.5 flex items-center gap-1.5 font-heading font-bold text-base tabular-nums" data-testid="analisi-countdown"><Loader2 className="h-4 w-4 animate-spin" /> {countLabel}</span></span>
              </div>
            )}
          </div>
        )}

        {editing && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              {["f1_kwh", "f2_kwh", "f3_kwh"].map((k, i) => (
                <div key={k} className="space-y-1.5">
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">{`F${i + 1} (kWh/anno)`}</label>
                  <Input type="number" value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`input-${k}`} placeholder="0" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Prezzo energia (€/kWh)</label>
                <Input type="number" step="0.01" value={form.prezzo_energia_kwh} onChange={(e) => setForm((f) => ({ ...f, prezzo_energia_kwh: e.target.value }))} data-testid="input-prezzo-energia" placeholder="0.30" />
                <p className="text-[11px] text-muted-foreground">Costo <b>effettivo</b> in bolletta: materia energia + trasporto/vettoriamento + oneri di sistema (IVA incl.), escluse le quote fisse.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Potenza contatore (kW)</label>
                <Input type="number" step="0.5" value={form.potenza_contatore_kw} onChange={(e) => setForm((f) => ({ ...f, potenza_contatore_kw: e.target.value }))} data-testid="input-potenza-contatore" placeholder="6" />
                <p className="text-[11px] text-muted-foreground">Potenza impegnata/disponibile del contatore.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={recalc} disabled={saving} data-testid="recalc-analysis-btn" className="font-semibold">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Ricalcola</Button>
              <Button variant="ghost" onClick={() => setEditing(false)}><X className="h-4 w-4 mr-1" /> Annulla</Button>
            </div>
          </div>
        )}

        {analysis && !editing && (
          <div className="space-y-5">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Fascia</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Consumo (kWh/anno)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="border-border"><TableCell className="font-medium">F1 · Ore di punta</TableCell><TableCell className="text-right font-heading" data-testid="analysis-f1">{fmt(analysis.f1_kwh)}</TableCell></TableRow>
                <TableRow className="border-border"><TableCell className="font-medium">F2 · Ore intermedie</TableCell><TableCell className="text-right font-heading" data-testid="analysis-f2">{fmt(analysis.f2_kwh)}</TableCell></TableRow>
                <TableRow className="border-border"><TableCell className="font-medium">F3 · Notturne/festivi</TableCell><TableCell className="text-right font-heading" data-testid="analysis-f3">{fmt(analysis.f3_kwh)}</TableCell></TableRow>
                <TableRow className="border-border bg-secondary/30 hover:bg-secondary/30"><TableCell className="font-bold">Consumo totale{analysis.consumo_annuo_stimato ? " (stimato)" : ""}</TableCell><TableCell className="text-right font-heading font-bold" data-testid="analysis-total">{fmt(analysis.consumo_annuo_kwh)}</TableCell></TableRow>
              </TableBody>
            </Table>

            {analysis.consumo_annuo_stimato && (
              <p className="text-xs text-warning" data-testid="estimate-note">
                Consumo annuo <b>stimato</b>: la bolletta riporta un periodo {PERIODO_LABEL[analysis.periodo] || analysis.periodo}, i valori F1/F2/F3 sono stati estrapolati su base annua ({PERIODO_FATTORE[analysis.periodo] || "×1"}).
              </p>
            )}

            <div className="border border-border rounded p-4 space-y-3" data-testid="analysis-prezzo-energia">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                  <Gauge className="h-4 w-4 text-primary" strokeWidth={1.5} /> Prezzo energia
                </div>
                <div className="text-right">
                  <div className="text-2xl font-heading font-bold">
                    {analysis.prezzo_energia_kwh ? Number(analysis.prezzo_energia_kwh).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : "0,30"}
                    <span className="text-sm font-normal text-muted-foreground"> €/kWh</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {analysis.prezzo_energia_kwh ? "Costo effettivo dalla bolletta (materia + trasporto + oneri) · usato per il risparmio" : "Valore predefinito (non rilevato) · usato per il risparmio"}
                  </div>
                </div>
              </div>
              {analysis.prezzo_componenti && (
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border" data-testid="prezzo-componenti">
                  {[["Materia energia", "materia_eur_kwh"], ["Trasporto/vettoriamento", "trasporto_eur_kwh"], ["Oneri di sistema", "oneri_eur_kwh"]].map(([lbl, k]) => (
                    <div key={k} className="rounded bg-secondary/30 p-2 text-center" data-testid={`prezzo-comp-${k}`}>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-tight">{lbl}</div>
                      <div className="text-sm font-heading font-bold mt-0.5">{analysis.prezzo_componenti[k] != null ? Number(analysis.prezzo_componenti[k]).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : "—"}<span className="text-[10px] font-normal text-muted-foreground"> €/kWh</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-border rounded p-4 flex items-center justify-between" data-testid="analysis-potenza-contatore">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Zap className="h-4 w-4 text-primary" strokeWidth={1.5} /> Potenza contatore
              </div>
              <div className="text-right">
                <div className="text-2xl font-heading font-bold">
                  {analysis.potenza_contatore_kw ? Number(analysis.potenza_contatore_kw).toLocaleString("it-IT", { maximumFractionDigits: 2 }) : "—"}
                  <span className="text-sm font-normal text-muted-foreground"> kW</span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {analysis.potenza_contatore_kw ? "Potenza impegnata/disponibile rilevata dalla bolletta" : "Non rilevata dalla bolletta"}
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold mb-3">Dimensionamento di massima</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-border rounded p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><SunMedium className="h-4 w-4 text-primary" strokeWidth={1.5} /> Impianto FV</div>
                  <div className="mt-2 text-2xl font-heading font-bold" data-testid="analysis-pv">{fmt(analysis.pv_kwp)} <span className="text-sm font-normal text-muted-foreground">kWp</span></div>
                </div>
                <div className="border border-border rounded p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><BatteryCharging className="h-4 w-4 text-primary" strokeWidth={1.5} /> Accumulo</div>
                  <div className="mt-2 text-2xl font-heading font-bold" data-testid="analysis-storage">{fmt(analysis.storage_kwh)} <span className="text-sm font-normal text-muted-foreground">kWh</span></div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {isPvgis
                  ? <>Resa reale da <b className="text-foreground">PVGIS</b>: {fmt(analysis.yield_factor)} kWh/kWp anno · {analysis.pvgis?.location}</>
                  : <>Stima con resa {fmt(analysis.yield_factor)} kWh/kWp anno. Calcola con PVGIS per affinare in base alla località.</>}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {hasBolletta && (
                <Button variant="outline" size="sm" onClick={analyze} disabled={analyzing} data-testid="reanalyze-bill-btn">
                  {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Ri-analizza bolletta
                </Button>
              )}
            </div>
            {analyzing && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-600 dark:text-amber-400" data-testid="reanalisi-alert" role="alert">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Analisi della bolletta in corso: <b>potrebbero volerci fino a 120 secondi</b>. Non chiudere la pagina.<span className="mt-1.5 flex items-center gap-1.5 font-heading font-bold text-base tabular-nums" data-testid="reanalisi-countdown"><Loader2 className="h-4 w-4 animate-spin" /> {countLabel}</span></span>
              </div>
            )}
          </div>
        )}
      </Card>

      {!editing && <LeadFornitura lead={lead} onUpdate={onUpdate} />}

      {/* Report energetico (motore bolletta) */}
      {lead.energy_analysis && !editing && (
        <EnergyReport ea={lead.energy_analysis} />
      )}

      {/* Componenti impianto (da catalogo Prodotti) */}
      {analysis && !editing && components && (components.pannello || components.batteria) && (
        <Card className="rounded border-border p-5" data-testid="lead-components">
          <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" strokeWidth={1.5} /> Componenti impianto</h2>
          <p className="text-xs text-muted-foreground mb-4">Dimensionamento in base ai Prodotti di flusso (potenza pannello e capacità batterie).</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {components.pannello && (
              <div className="border border-border rounded p-4" data-testid="component-pannello">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><SunMedium className="h-4 w-4 text-primary" strokeWidth={1.5} /> Pannelli</div>
                <div className="mt-2 text-2xl font-heading font-bold">{components.pannello.n_pannelli} <span className="text-sm font-normal text-muted-foreground">moduli</span></div>
                <div className="text-xs text-muted-foreground mt-1">{components.pannello.model} · {components.pannello.potenza_wp} Wp · {components.pannello.potenza_installata_kwp} kWp installati</div>
              </div>
            )}
            {components.batteria && (
              <div className="border border-border rounded p-4" data-testid="component-batteria">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><BatteryCharging className="h-4 w-4 text-primary" strokeWidth={1.5} /> Accumulo <Badge variant="outline" className="border-primary/40 text-primary font-normal ml-1">{components.batteria.tipo}</Badge></div>
                <div className="mt-2 text-2xl font-heading font-bold">{components.batteria.n_batterie} <span className="text-sm font-normal text-muted-foreground">batterie</span></div>
                <div className="text-xs text-muted-foreground mt-1">{components.batteria.model} · {components.batteria.capacita_modulo_kwh} kWh/modulo · {components.batteria.capacita_totale_kwh} kWh totali</div>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Regola batterie: <b>LV</b> per 1-2 batterie, <b>HV</b> per 3 o più batterie.</p>
        </Card>
      )}

      {/* PVGIS geolocation */}
      {analysis && !editing && (
        <Card className="rounded border-border p-5" data-testid="pvgis-card">
          <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" strokeWidth={1.5} /> Geolocalizzazione &amp; Produzione (PVGIS)</h2>
          <p className="text-xs text-muted-foreground mb-2">Calcola la produzione fotovoltaica reale in base alla località (fonte: PVGIS - Commissione Europea).</p>
          <div className="flex items-start gap-2 mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2" data-testid="pvgis-city-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 text-red-500 mt-0.5" strokeWidth={2} />
            <p className="text-xs font-medium text-red-500 leading-snug m-0">Inserisci solo la <b>città o il comune</b> (es. Brescia), non l'indirizzo completo.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Città o comune (es. Brescia)" data-testid="pvgis-address-input" />
            <Button onClick={runPvgis} disabled={geo} data-testid="run-pvgis-btn" className="font-semibold shrink-0">
              {geo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />} Calcola con PVGIS
            </Button>
          </div>

          {analysis.accumulo && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="border border-border rounded p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Mesi con eccedenza su F1</div>
                <div className="mt-2 text-2xl font-heading font-bold" data-testid="accumulo-mesi">{analysis.accumulo.mesi_eccedenza} <span className="text-sm font-normal text-muted-foreground">/ 12</span></div>
              </div>
              <div className="border border-border rounded p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Energia destinabile all'accumulo</div>
                <div className="mt-2 text-2xl font-heading font-bold" data-testid="accumulo-energia">{fmt(analysis.accumulo.energia_eccedente_kwh)} <span className="text-sm font-normal text-muted-foreground">kWh/anno</span></div>
              </div>
            </div>
          )}

          {monthly && monthly.length === 12 && (
            <div className="mt-5">
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold">Bilancio mensile: produzione FV vs consumo F1</div>
                {analysis.f1_distribution && analysis.f1_distribution !== "consumi_mensili" && (
                  <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 font-normal text-[11px]" data-testid="consumo-stima-badge">
                    Distribuzione mensile stimata
                  </Badge>
                )}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead className="text-xs uppercase">Mese</TableHead>
                      <TableHead className="text-xs uppercase text-right">Produzione FV</TableHead>
                      <TableHead className="text-xs uppercase text-right">Consumo F1 (stim.)</TableHead>
                      <TableHead className="text-xs uppercase text-right">Eccedenza → Accumulo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthly.map((m) => (
                      <TableRow key={m.mese} className="border-border" data-testid={`month-row-${m.mese}`}>
                        <TableCell className="font-medium">{MONTHS_IT[m.mese - 1]}</TableCell>
                        <TableCell className="text-right font-heading">{fmt(m.produzione_kwh)}</TableCell>
                        <TableCell className="text-right font-heading text-muted-foreground">{fmt(m.consumo_f1_kwh)}</TableCell>
                        <TableCell className={`text-right font-heading ${m.eccedenza_kwh > 0 ? "text-success" : "text-muted-foreground"}`}>{m.eccedenza_kwh > 0 ? `+${fmt(m.eccedenza_kwh)}` : "0"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-2" data-testid="consumo-distribuzione-nota">
                <b>Produzione FV</b>: dati reali PVGIS per la località. <b>Consumo F1</b>: {analysis.f1_distribution === "consumi_mensili"
                  ? "ripartito secondo i consumi mensili effettivamente rilevati sulla bolletta."
                  : analysis.f1_distribution === "stima_commerciale"
                    ? "⚠️ la bolletta non riporta il dettaglio mensile: il consumo annuo F1 è stato distribuito con una curva stagionale stimata per profilo commerciale/aziendale."
                    : analysis.f1_distribution === "stima_residenziale"
                      ? "⚠️ la bolletta non riporta il dettaglio mensile: il consumo annuo F1 è stato distribuito con una curva stagionale stimata per profilo residenziale."
                      : "⚠️ la bolletta non riporta il dettaglio mensile: il consumo annuo F1 è stato distribuito in modo stimato sui 12 mesi (ri-esegui l'analisi per applicare la curva stagionale aggiornata)."} L'accumulo viene impiegato nei mesi in cui la produzione FV eccede il consumo in fascia F1, immagazzinando l'energia in surplus.
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Monthly consumption from bill */}
      {monthlyCons && monthlyCons.length > 0 && (
        <Card className="rounded border-border p-5" data-testid="monthly-consumption-card">
          <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" strokeWidth={1.5} /> Consumi mensili (da bolletta)</h2>
          <p className="text-xs text-muted-foreground mb-4">Dettaglio dei consumi mensili rilevati sulla bolletta.</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase">Mese</TableHead>
                  <TableHead className="text-xs uppercase text-right">Consumo (kWh)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyCons.map((m) => (
                  <TableRow key={m.mese} className="border-border">
                    <TableCell className="font-medium">{MONTHS_IT[m.mese - 1] || m.mese}</TableCell>
                    <TableCell className="text-right font-heading">{fmt(m.kwh)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
